import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { map, catchError, of } from 'rxjs';
import { MaskService } from '@shared/services/mask.service';
import { SelectPesquisavelService } from '@shared/components/select-pesquisavel/select-pesquisavel.service';
import {
    PesquisaCodigoTributacaoModalComponent,
    PesquisaCodigoTributacaoModalData,
    PesquisaCodigoTributacaoModalResult,
} from '@shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { FinalidadeNFeStruct } from '@shared/services/structs/motor-fiscal/finalidade-nfe.struct';
import { RegimeTributarioStruct } from '@shared/services/structs/motor-fiscal/regime-tributario.struct';
import { TipoOperacaoStruct } from '@shared/services/structs/motor-fiscal/tipo-operacao.struct';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { TipoPadraoNaturezaEnum } from '@shared/enums/motor-fiscal/tipo-padrao-natureza.enum';
import { TipoPadraoNaturezaOperacaoStruct } from '@shared/services/structs/motor-fiscal/tipo-padrao-natureza-operacao.struct';
import { TipoAmbienteEnum } from '@shared/enums/dfe/tipo-ambiente.enum';
import { EmissorElegivelStruct } from '@shared/services/structs/dfe/emissor-elegivel.struct';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { environment } from '../../../../../../../environments/environment';

/** Opção do select de emissor: o elegível com o texto "{nome} · Série {serie} · {ambiente}". */
type EmissorElegivelOpcao = EmissorElegivelStruct & { descricaoExibicao: string };

@Component({
    selector: 'app-natureza-operacao-dados-gerais-tab',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatRadioModule,
        MatDividerModule,
        MatButtonModule,
        MatTooltipModule,
        NgxMaskDirective,
        SelectPesquisavelComponent,
    ],
    providers: [provideNgxMask()],
    templateUrl: './natureza-operacao-dados-gerais-tab.component.html',
    styleUrl: './natureza-operacao-dados-gerais-tab.component.css',
})
export class NaturezaOperacaoDadosGeraisTabComponent implements OnChanges {
    @Input({ required: true }) form!: FormGroup;
    @Input() finalidades: FinalidadeNFeStruct[] = [];
    @Input() regimes: RegimeTributarioStruct[] = [];
    @Input() tiposOperacao: TipoOperacaoStruct[] = [];
    /** Emissores ativos com NF-e ativa. A série e o ambiente vêm do emissor, nunca da natureza. */
    @Input() emissores: EmissorElegivelStruct[] = [];
    /** Em edição, tipo de operação e regime tributário ficam bloqueados (definidos na criação). */
    @Input() isEdicao = false;
    /** Catálogo dos tipos de padrão ativos, com a natureza que hoje é o padrão de cada tipo. */
    @Input() tiposPadrao: TipoPadraoNaturezaOperacaoStruct[] = [];
    /** Padrões gravados desta natureza: marcá-los não substitui ninguém. */
    @Input() tiposPadraoIniciais: TipoPadraoNaturezaEnum[] = [];

    public readonly TipoOperacaoEnum = TipoOperacaoEnum;
    public cfopLookupUrl = environment.urlApiMotorFiscal + 'Lookup/cfop';
    public readonly masks = MaskService.getMasks();
    /** Permite o coringa "?" na 1ª posição do CFOP (a regra por posição é garantida no onCfopKeydown). */
    public readonly cfopPatterns = { '0': { pattern: /[0-9?]/ } };

    public readonly cfopEntradaTooltipInfo =
        'Use "?" no início do CFOP\n' +
        'para substituição automática\n' +
        'conforme a UF do fornecedor.\n\n' +
        'Exemplo: Para o CFOP ?102,\n' +
        'o "?" será substituído por:\n\n' +
        '1 - Operação dentro da mesma UF\n' +
        '2 - Operação interestadual\n' +
        '3 - Operação de importação';

    public readonly cfopSaidaTooltipInfo =
        'Use "?" no início do CFOP\n' +
        'para substituição automática\n' +
        'conforme a UF do cliente.\n\n' +
        'Exemplo: Para o CFOP ?102,\n' +
        'o "?" será substituído por:\n\n' +
        '5 - Operação dentro da mesma UF\n' +
        '6 - Operação interestadual\n' +
        '7 - Operação de exportação';

    public emissoresOpcoes: EmissorElegivelOpcao[] = [];

    private readonly descricaoAmbiente: Record<number, string> = {
        [TipoAmbienteEnum.Producao]: 'Produção',
        [TipoAmbienteEnum.Homologacao]: 'Homologação',
    };

    constructor(
        private dialog: MatDialog,
        private selectPesquisavelService: SelectPesquisavelService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['emissores']) {
            this.emissoresOpcoes = (this.emissores ?? []).map(emissor => ({
                ...emissor,
                descricaoExibicao: this.descreverEmissor(emissor),
            }));
        }
    }

    public get emissorControl(): FormControl {
        return this.form.get('idEmissor') as FormControl;
    }

    public get emissorSelecionado(): EmissorElegivelStruct | null {
        const idEmissor = this.emissorControl?.value;
        return idEmissor == null ? null : this.emissores.find(emissor => emissor.idEmissor === idEmissor) ?? null;
    }

    /** Emissor gravado que já não está entre os elegíveis (inativo, sem NF-e ativa ou excluído). */
    public get isEmissorIndisponivel(): boolean {
        return this.emissorControl?.value != null && !this.emissorSelecionado;
    }

    public get hintEmissor(): string {
        if (this.emissores.length === 0) {
            return 'Não há emissores ativos com NF-e ativa. O emissor padrão será usado.';
        }
        if (this.isEmissorIndisponivel) {
            return 'O emissor informado nesta natureza está indisponível. Selecione outro ou deixe em branco para usar o emissor padrão.';
        }
        return 'Opcional. Selecione o emissor para esta natureza. Em branco, será usado o emissor padrão.';
    }

    public descricaoAmbienteEmissor(emissor: EmissorElegivelStruct): string {
        return this.descricaoAmbiente[emissor.idTipoAmbiente] ?? '';
    }

    private descreverEmissor(emissor: EmissorElegivelStruct): string {
        const partes = [emissor.nome];
        if (emissor.serie != null) partes.push(`Série ${emissor.serie}`);
        partes.push(this.descricaoAmbiente[emissor.idTipoAmbiente] ?? '');
        return partes.filter(Boolean).join(' · ');
    }

    public get tipoOperacaoValue(): TipoOperacaoEnum | null {
        return this.form?.get('tipoOperacao')?.value ?? null;
    }

    /** "Padrão para" só oferece os tipos de padrão do tipo de operação da natureza. */
    public get tiposPadraoDoTipoOperacao(): TipoPadraoNaturezaOperacaoStruct[] {
        return this.tiposPadrao.filter(tipo => tipo.idTipoOperacao === this.tipoOperacaoValue);
    }

    /** O que muda ao salvar: outra natureza que perde o padrão marcado aqui e padrão gravado que foi desmarcado. */
    public get hintPadroes(): string {
        const selecionados: TipoPadraoNaturezaEnum[] = this.form.get('tiposPadrao')?.value ?? [];
        const avisos: string[] = [];
        for (const tipo of this.tiposPadraoDoTipoOperacao) {
            const id = tipo.idTipoPadraoNaturezaOperacao;
            const isInicial = this.tiposPadraoIniciais.includes(id);
            if (selecionados.includes(id) && !isInicial && tipo.idNaturezaOperacaoAtual != null) {
                avisos.push(`Hoje: ${tipo.nomeNaturezaOperacaoAtual}. Ao salvar, ${tipo.nomeNaturezaOperacaoAtual} deixa de ser o ${tipo.nome}.`);
            }
            if (!selecionados.includes(id) && isInicial) {
                avisos.push(`Ao salvar, esta natureza deixa de ser o ${tipo.nome}.`);
            }
        }
        return avisos.length > 0
            ? avisos.join(' ')
            : 'Opcional. A natureza padrão vem sugerida nos documentos do tipo marcado.';
    }

    public get cfopControl(): FormControl {
        return this.form.get('cfopPadrao') as FormControl;
    }

    /** Tooltip do "?" conforme o tipo de operação (entrada usa 1/2/3, saída usa 5/6/7). */
    public get cfopTooltipInfo(): string {
        return this.tipoOperacaoValue === TipoOperacaoEnum.Entrada ? this.cfopEntradaTooltipInfo : this.cfopSaidaTooltipInfo;
    }

    /** Dígitos válidos para a 1ª posição do CFOP conforme o tipo de operação. */
    public get cfopPrimeirosDigitos(): string[] {
        return this.tipoOperacaoValue === TipoOperacaoEnum.Entrada ? ['1', '2', '3'] : ['5', '6', '7'];
    }

    public get cfopPlaceholder(): string {
        return this.tipoOperacaoValue === TipoOperacaoEnum.Entrada ? 'Ex: 1102, ?102, ?101' : 'Ex: 5102, ?102, ?101';
    }

    /**
     * Valida entrada no campo CFOP. Na primeira posição aceita "?" ou apenas os dígitos válidos
     * do tipo de operação (entrada: 1/2/3; saída: 5/6/7); nas demais posições aceita qualquer
     * dígito. O tamanho (4 dígitos) e o ponto da máscara são controlados pelo ngx-mask.
     */
    public onCfopKeydown(event: KeyboardEvent): void {
        const input = event.target as HTMLInputElement;
        const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        const isShortcut = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
        if (allowed.includes(event.key) || isShortcut) return;

        const selStart = input.selectionStart ?? 0;

        // Primeira posição: aceita "?" ou apenas os dígitos válidos do tipo de operação.
        if (selStart === 0) {
            if (event.key === '?' || this.cfopPrimeirosDigitos.includes(event.key)) return;
            event.preventDefault();
            return;
        }

        // Demais posições: somente dígitos.
        if (!/^\d$/.test(event.key)) { event.preventDefault(); }
    }

    public pesquisarCfop(): void {
        const cfopUrl = `${this.cfopLookupUrl}?idTipoOperacao=${this.tipoOperacaoValue}`;
        const dialogRef = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
            width: '700px',
            maxHeight: '85vh',
            panelClass: 'pesquisa-codigo-tributacao-dialog',
            data: {
                titulo: 'Pesquisar CFOP',
                buscarFn: (pesquisa: string) =>
                    this.selectPesquisavelService.getSelectValues<any>(cfopUrl, pesquisa).pipe(
                        map(list => ({
                            isError: false,
                            itens: list.map((i: any) => ({ codigo: String(i.codigo ?? ''), descricao: i.descricao })),
                        })),
                        catchError(() => of({ isError: true, itens: [] })),
                    ),
                permitirManual: false,
                registrosIniciais: 20,
                valorInicial: this.cfopControl?.value ?? undefined,
            } as PesquisaCodigoTributacaoModalData,
        });

        dialogRef.afterClosed().subscribe((result: PesquisaCodigoTributacaoModalResult | null) => {
            if (!result?.selecionado) return;
            this.cfopControl?.setValue(result.selecionado.codigo.replace(/\D/g, ''));
        });
    }
}
